/** Mirrors GET t/:slug/api/v1/tenant-settings. Missing server rows mean current behaviour. */

export type TenantVatMode = 'enabled' | 'disabled' | 'skipped';
export type AmountDisplayUnit = 'toman' | 'rial';
export type TenantPersonType = 'individual' | 'company' | 'undecided';
export type SalesModelSetting = 'b2b' | 'b2c' | 'both';
export type QuoteInProcessSetting = 'yes' | 'no' | 'later';
export type PrRequirementSetting = 'always' | 'sometimes' | 'never';

export interface TenantLegalIdentity {
  readonly invoiceDisplayName: string | null;
  readonly legalName: string | null;
  readonly nationalId: string | null;
  readonly ownerFirstName: string | null;
  readonly ownerLastName: string | null;
  readonly economicCode: string | null;
  readonly phone: string | null;
  readonly address: string | null;
}

export interface TenantSettings {
  readonly vat: TenantVatMode;
  readonly amountDisplayUnit: AmountDisplayUnit;
  readonly personType: TenantPersonType | null;
  readonly legalIdentity: TenantLegalIdentity | null;
  readonly salesModel: SalesModelSetting | null;
  readonly quoteInProcess: QuoteInProcessSetting;
  readonly serialTracking: boolean;
  readonly prRequirement: PrRequirementSetting;
  readonly documentNumbering: {
    readonly prefix: string;
    readonly padding: number;
    readonly series: string;
  } | null;
  readonly managesInventory: 'yes' | 'no' | 'later' | null;
}

export interface TenantSettingsResponse {
  readonly settings: TenantSettings;
}

export const TENANT_SETTING_DEFAULTS: TenantSettings = {
  vat: 'enabled',
  amountDisplayUnit: 'rial',
  personType: null,
  legalIdentity: null,
  salesModel: null,
  quoteInProcess: 'yes',
  serialTracking: true,
  prRequirement: 'sometimes',
  documentNumbering: null,
  managesInventory: null,
};

export function vatEnabled(settings: TenantSettings): boolean {
  return settings.vat === 'enabled';
}

export function quotesVisible(settings: TenantSettings): boolean {
  return settings.quoteInProcess !== 'no';
}

export function purchaseRequestsVisible(settings: TenantSettings): boolean {
  return settings.prRequirement !== 'never';
}

export function directPurchaseOrderAllowed(settings: TenantSettings): boolean {
  return settings.prRequirement !== 'always';
}

export function defaultPartyKind(
  salesModel: SalesModelSetting | null,
): 'person' | 'organization' | '' {
  if (salesModel === 'b2b') return 'organization';
  if (salesModel === 'b2c') return 'person';
  return '';
}
